import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ListParams } from '@/types/api'
import type { StaffFormOutput, StaffAccountInput } from './schemas'
import type { NamedItem, StaffMember, StaffStatus } from './types'

export const staffKeys = {
  all: ['staff'] as const,
  list: (params: object) => [...staffKeys.all, 'list', params] as const,
  detail: (id: number) => [...staffKeys.all, 'detail', id] as const,
  departments: () => [...staffKeys.all, 'departments'] as const,
  designations: () => [...staffKeys.all, 'designations'] as const,
}

export const staffListQuery = (params: ListParams & Record<string, string | number | undefined>) =>
  queryOptions({
    queryKey: staffKeys.list(params),
    queryFn: () => api.page<StaffMember>('/staff', params),
    placeholderData: keepPreviousData,
  })
export const staffQuery = (id: number) =>
  queryOptions({ queryKey: staffKeys.detail(id), queryFn: () => api.get<StaffMember>(`/staff/${id}`) })
export const departmentsQuery = () =>
  queryOptions({ queryKey: staffKeys.departments(), queryFn: () => api.get<NamedItem[]>('/departments') })
export const designationsQuery = () =>
  queryOptions({ queryKey: staffKeys.designations(), queryFn: () => api.get<NamedItem[]>('/designations') })

function useStaffMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export const useSaveStaff = () =>
  useStaffMutation(({ id, ...payload }: StaffFormOutput & { id?: number }) =>
    id ? api.put<StaffMember>(`/staff/${id}`, payload) : api.post<StaffMember>('/staff', payload),
  )
export const useChangeStaffStatus = () =>
  useStaffMutation(({ id, status }: { id: number; status: StaffStatus }) =>
    api.patch<StaffMember>(`/staff/${id}/status`, { status }),
  )
export const useCreateStaffAccount = () =>
  useStaffMutation(({ id, ...body }: StaffAccountInput & { id: number }) =>
    api.post<StaffMember>(`/staff/${id}/account`, body),
  )

export type SetupKind = 'departments' | 'designations'
export const useSaveSetupItem = (kind: SetupKind) =>
  useStaffMutation(({ id, name }: { id?: number; name: string }) =>
    id ? api.put<NamedItem>(`/${kind}/${id}`, { name }) : api.post<NamedItem>(`/${kind}`, { name }),
  )
export const useDeleteSetupItem = (kind: SetupKind) =>
  useStaffMutation((id: number) => api.delete(`/${kind}/${id}`))

export const setupItemsQuery = (kind: SetupKind) =>
  queryOptions({
    queryKey: [...staffKeys.all, kind] as const,
    queryFn: () => api.get<NamedItem[]>(`/${kind}`),
  })
